import React from 'react';
import {
  CheckCircle,
  Loader2,
  Network,
  Download,
  Sparkles,
  ClipboardCheck,
  Upload,
  CheckCheck,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import type { PartCategoryStage } from '../../types';

interface PartCategoryStageIndicatorProps {
  currentStage: PartCategoryStage;
  stageName: string;
  stageProgress?: number;
}

interface StageDefinition {
  id: PartCategoryStage;
  label: string;
  icon: React.ElementType;
}

const STAGES: StageDefinition[] = [
  { id: 'taxonomy_fetch',    label: 'Taxonomy',   icon: Network       },
  { id: 'parts_fetch',       label: 'Fetch Parts', icon: Download      },
  { id: 'ai_categorisation', label: 'AI Tag',      icon: Sparkles      },
  { id: 'awaiting_validation', label: 'Review',    icon: ClipboardCheck },
  { id: 'write_back',        label: 'Write Back',  icon: Upload        },
  { id: 'complete',          label: 'Complete',    icon: CheckCheck    },
];

type StageStatus = 'completed' | 'active' | 'pending';

export const PartCategoryStageIndicator: React.FC<PartCategoryStageIndicatorProps> = ({
  currentStage,
  stageName,
  stageProgress = 0,
}) => {
  const currentIdx = STAGES.findIndex(s => s.id === currentStage);

  const getStatus = (idx: number): StageStatus => {
    if (idx < currentIdx) return 'completed';
    if (idx === currentIdx) {
      // 'complete' is a terminal stage — show as completed, not active
      if (STAGES[idx].id === 'complete') return 'completed';
      return 'active';
    }
    return 'pending';
  };

  const getIcon = (stage: StageDefinition, idx: number) => {
    const status = getStatus(idx);
    if (status === 'completed') return <CheckCircle className="w-5 h-5 text-qc-success" />;
    if (status === 'active') {
      if (stage.id === 'awaiting_validation') {
        return <ClipboardCheck className="w-5 h-5 text-text-secondary" />;
      }
      return <Loader2 className="w-5 h-5 text-text-secondary animate-spin" />;
    }
    const Icon = stage.icon;
    return <Icon className="w-5 h-5 text-text-tertiary" />;
  };

  return (
    <div className="bg-bg-surface border border-border rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-semibold ${theme.text.primary}`}>Processing Stages</h3>
        <span className={`text-xs ${theme.text.tertiary}`}>
          {stageName}
          {currentStage === 'ai_categorisation' && stageProgress > 0
            ? ` — ${stageProgress}%`
            : ''}
        </span>
      </div>

      {/* Progress bar — only shown during ai_categorisation */}
      {currentStage === 'ai_categorisation' && (
        <div className="w-full h-1.5 bg-bg-hover rounded-full mb-4">
          <div
            className="h-1.5 bg-interactive-primary rounded-full transition-all duration-500"
            style={{ width: `${Math.min(stageProgress, 100)}%` }}
          />
        </div>
      )}

      {/* Desktop: horizontal flow */}
      <div className="hidden md:flex items-center gap-2">
        {STAGES.map((stage, idx) => {
          const status = getStatus(idx);
          const isLast = idx === STAGES.length - 1;

          return (
            <React.Fragment key={stage.id}>
              <div className="flex flex-col items-center">
                <div
                  className={[
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    status === 'completed' ? 'bg-qc-success/10' : '',
                    status === 'active' && stage.id !== 'awaiting_validation'
                      ? 'bg-bg-subtle ring-2 ring-border-strong'
                      : '',
                    status === 'active' && stage.id === 'awaiting_validation'
                      ? 'bg-bg-subtle ring-2 ring-border-strong'
                      : '',
                    status === 'pending' ? 'bg-bg-subtle' : '',
                  ].join(' ')}
                >
                  {getIcon(stage, idx)}
                </div>
                <span
                  className={`text-xs mt-1 font-medium ${
                    status === 'active' ? theme.text.primary : theme.text.tertiary
                  }`}
                >
                  {stage.label}
                </span>
              </div>
              {!isLast && <div className="flex-shrink-0 w-8 h-0.5 bg-border-default" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile: vertical list */}
      <div className="md:hidden space-y-2">
        {STAGES.map((stage, idx) => {
          const status = getStatus(idx);
          return (
            <div key={stage.id} className="flex items-center gap-3">
              <div
                className={[
                  'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                  status === 'completed' ? 'bg-qc-success/10' : '',
                  status === 'active' && stage.id !== 'awaiting_validation'
                    ? 'bg-bg-subtle ring-2 ring-border-strong'
                    : '',
                  status === 'active' && stage.id === 'awaiting_validation'
                    ? 'bg-bg-subtle ring-2 ring-border-strong'
                    : '',
                  status === 'pending' ? 'bg-bg-subtle' : '',
                ].join(' ')}
              >
                {getIcon(stage, idx)}
              </div>
              <span
                className={`text-sm ${
                  status === 'active' ? `font-semibold ${theme.text.primary}` : theme.text.tertiary
                }`}
              >
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>

    </div>
  );
};
